/*
 * Knuska collection tabs.
 *
 * Liquid renders an ordinary list of links to a stack of open panels, which is
 * a complete section on its own. This file upgrades that to a tablist and
 * closes all but one panel. Everything it does is additive, so a script that
 * fails to load costs a reader nothing but the tabs.
 *
 * The roles are applied here rather than in the markup on purpose. A tab role
 * on an element with no script behind it is a lie told to a screen reader -
 * announced as a tab, operable as a link, and the panels it claims to control
 * all open at once. Roles arrive with the behaviour that makes them true.
 */
if (!customElements.get('knuska-collection-tabs')) {
  const REDUCED = '(prefers-reduced-motion: reduce)';

  class KnuskaCollectionTabs extends HTMLElement {
    connectedCallback() {
      this.tablist = this.querySelector('[data-kct-tablist]');
      this.strip = this.querySelector('[data-kct-tabs]');
      this.indicator = this.querySelector('[data-kct-indicator]');
      this.tabs = Array.from(this.querySelectorAll('[data-kct-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-kct-panel]'));
      if (this.tabs.length < 2 || !this.panels.length) return;

      this.upgrade();

      /*
       * A hash pointing at one of these panels is a deep link to that tab, and
       * the browser has already tried to scroll to a panel that is about to be
       * closed - so honour it rather than fight it.
       */
      const target = this.fromHash();
      this.select(target || this.tabs[0], false);

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.tablist.addEventListener('click', this.onClick);
      this.tablist.addEventListener('keydown', this.onKeydown);

      /*
       * Label widths change when the real font lands, and the indicator is sized
       * from them - so one measured before that is measured against metrics the
       * reader never sees.
       */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.track());
      }

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.schedule());
        this.observer.observe(this);
      }

      /* Selecting a tab in the theme editor should open the tab it belongs to. */
      this.onBlockSelect = (event) => {
        const tab = this.tabs.find((t) => event.detail.blockId && t.id.indexOf(event.detail.blockId) !== -1);
        if (tab) this.select(tab, false);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.tablist) {
        this.tablist.removeEventListener('click', this.onClick);
        this.tablist.removeEventListener('keydown', this.onKeydown);
      }
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    upgrade() {
      this.tablist.setAttribute('role', 'tablist');
      if (this.dataset.label) this.tablist.setAttribute('aria-label', this.dataset.label);

      this.tabs.forEach((tab) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', tab.dataset.kctTab);
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '-1');
      });

      this.panels.forEach((panel) => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', panel.dataset.kctPanel + '-tab');
        /*
         * A panel is a large region with no focusable wrapper of its own; making
         * it focusable is what lets keyboard focus land in it after a tab is
         * chosen. -1 keeps it out of the tab sequence itself.
         */
        panel.setAttribute('tabindex', '-1');
        panel.hidden = true;
      });

      /* The list items are presentation once the list is a tablist. */
      Array.from(this.tablist.children).forEach((li) => li.setAttribute('role', 'presentation'));

      this.classList.add('is-tabbed');
    }

    fromHash() {
      const hash = window.location.hash.slice(1);
      if (!hash) return null;
      return this.tabs.find((tab) => tab.dataset.kctTab === hash) || null;
    }

    onClick(event) {
      const tab = event.target.closest('[data-kct-tab]');
      if (!tab || !this.tabs.includes(tab)) return;
      /*
       * The href is a real link to a real panel, so it stays useful without
       * script. With script, jumping the page to the panel is the wrong
       * behaviour - the panel is already in view.
       */
      event.preventDefault();
      this.select(tab, true);
    }

    onKeydown(event) {
      const current = this.tabs.indexOf(document.activeElement);
      if (current === -1) return;

      let next = null;
      switch (event.key) {
        case 'ArrowRight':
          next = (current + 1) % this.tabs.length;
          break;
        case 'ArrowLeft':
          next = (current - 1 + this.tabs.length) % this.tabs.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = this.tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.select(this.tabs[next], true, true);
    }

    /*
     * move  - the choice came from the reader, so focus follows it.
     * toTab - keep focus on the tab (arrow keys) rather than sending it into the
     *         panel (a click), which is what the tabs pattern asks for.
     */
    select(tab, move, toTab) {
      if (!tab) return;

      this.tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
        t.classList.toggle('is-active', on);
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.kctPanel !== tab.dataset.kctTab;
      });

      this.active = tab;
      this.track();
      this.reveal(tab);

      if (move) {
        if (toTab) tab.focus();
        else {
          const panel = this.panels.find((p) => p.dataset.kctPanel === tab.dataset.kctTab);
          if (panel) panel.focus({ preventScroll: true });
        }
      }
    }

    /* Keep the chosen tab in view when the strip is scrolled sideways. */
    reveal(tab) {
      if (!this.strip || this.strip.scrollWidth <= this.strip.clientWidth) return;
      const smooth = !(window.matchMedia && window.matchMedia(REDUCED).matches);
      const left = tab.offsetLeft - (this.strip.clientWidth - tab.offsetWidth) / 2;
      this.strip.scrollTo({ left: Math.max(0, left), behavior: smooth ? 'smooth' : 'auto' });
    }

    schedule() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.track();
      });
    }

    /*
     * The indicator is measured from the active tab's own box rather than
     * computed from a count and a gap, so it is right for labels of any length
     * at any font size. offsetLeft is relative to the scrolling strip, which is
     * also what the indicator is positioned in - so it stays put when the strip
     * is scrolled instead of drifting off its tab.
     */
    track() {
      if (!this.indicator || !this.active) return;
      const width = this.active.offsetWidth;
      if (!width) return;
      this.style.setProperty('--kct-ind-w', width + 'px');
      this.style.setProperty('--kct-ind-x', this.active.offsetLeft + 'px');
      this.classList.add('is-tracking');
    }
  }

  customElements.define('knuska-collection-tabs', KnuskaCollectionTabs);
}
